/** Note naming, frequency maths, rank transposition and temperature effects. */

import { offsetForPitchClass, type Temperament } from "./temperaments.js";

/** MIDI note number of A4, the conventional tuning anchor. */
export const A4_MIDI = 69;

/**
 * Pitch-class names spelled the way an organ tuner reads a chest: flats where
 * the chain of fifths runs flat, sharps where it runs sharp.
 */
export const NOTE_NAMES = [
  "C",
  "C♯",
  "D",
  "E♭",
  "E",
  "F",
  "F♯",
  "G",
  "G♯",
  "A",
  "B♭",
  "B",
] as const;

export interface Tuning {
  /** Frequency in Hz assigned to the anchor note. */
  readonly referenceHz: number;
  /** MIDI note the reference frequency belongs to. Conventionally A4 = 69. */
  readonly anchorMidi: number;
  readonly temperament: Temperament;
}

export function pitchClassOf(midi: number): number {
  return ((Math.round(midi) % 12) + 12) % 12;
}

export function noteName(midi: number): string {
  return NOTE_NAMES[pitchClassOf(midi)]!;
}

/** Scientific pitch notation octave: C4 is middle C, MIDI 60. */
export function octaveOf(midi: number): number {
  return Math.floor(Math.round(midi) / 12) - 1;
}

export function noteLabel(midi: number): string {
  return `${noteName(midi)}${octaveOf(midi)}`;
}

/** Cents this note sits away from equal temperament under the given tuning. */
export function temperamentOffset(midi: number, tuning: Tuning): number {
  return offsetForPitchClass(
    tuning.temperament,
    pitchClassOf(midi),
    pitchClassOf(tuning.anchorMidi),
  );
}

/** Target frequency of a MIDI note under the given tuning. */
export function frequencyOf(midi: number, tuning: Tuning): number {
  const equal = tuning.referenceHz * Math.pow(2, (midi - tuning.anchorMidi) / 12);
  return equal * Math.pow(2, temperamentOffset(midi, tuning) / 1200);
}

/** Signed interval in cents from `reference` to `frequency`. */
export function centsBetween(reference: number, frequency: number): number {
  return 1200 * Math.log2(frequency / reference);
}

export interface NoteMatch {
  readonly midi: number;
  /** How far the measured frequency sits above (+) or below (-) the target. */
  readonly cents: number;
  readonly targetHz: number;
}

/**
 * Find the note whose target frequency is closest to `frequency`.
 *
 * Unequal temperaments shift notes by up to ~30 cents, so the nearest note is
 * not simply the rounded equal-tempered index; candidates either side are
 * checked explicitly.
 */
export function nearestNote(frequency: number, tuning: Tuning, searchRadius = 2): NoteMatch | null {
  if (!(frequency > 0) || !Number.isFinite(frequency)) return null;

  const approx = tuning.anchorMidi + centsBetween(tuning.referenceHz, frequency) / 100;
  const centre = Math.round(approx);

  let best: NoteMatch | null = null;
  for (let midi = centre - searchRadius; midi <= centre + searchRadius; midi++) {
    if (midi < 0 || midi > 127) continue;
    const targetHz = frequencyOf(midi, tuning);
    const cents = centsBetween(targetHz, frequency);
    if (best === null || Math.abs(cents) < Math.abs(best.cents)) {
      best = { midi, cents, targetHz };
    }
  }
  return best;
}

/** Deviation in cents of `frequency` from a specific note's target. */
export function centsFromNote(frequency: number, midi: number, tuning: Tuning): number {
  return centsBetween(frequencyOf(midi, tuning), frequency);
}

export interface Rank {
  readonly id: string;
  readonly label: string;
  /** Semitones the rank sounds above written pitch. */
  readonly semitones: number;
  readonly note: string;
}

/**
 * Standard rank pitches. The offsets are what the pipe sounds relative to the
 * key you press. Mutation ranks (5 1/3', 2 2/3', 1 3/5', 1 1/3') are in
 * practice tuned to the harmonic series of their unison rank rather than to
 * equal-tempered intervals; the offsets below place you at the right pipe, and
 * for the tierce especially you should expect the just interval to read flat of
 * the tempered one.
 */
export const RANKS: readonly Rank[] = [
  { id: "32", label: "32′", semitones: -24, note: "Sub-unison" },
  { id: "16", label: "16′", semitones: -12, note: "Sub-unison" },
  { id: "10-2-3", label: "10⅔′", semitones: -5, note: "Quint below" },
  { id: "8", label: "8′", semitones: 0, note: "Unison — sounds as written" },
  { id: "5-1-3", label: "5⅓′", semitones: 7, note: "Quint" },
  { id: "4", label: "4′", semitones: 12, note: "Octave" },
  { id: "2-2-3", label: "2⅔′", semitones: 19, note: "Twelfth (nazard)" },
  { id: "2", label: "2′", semitones: 24, note: "Fifteenth" },
  { id: "1-3-5", label: "1⅗′", semitones: 28, note: "Seventeenth (tierce)" },
  { id: "1-1-3", label: "1⅓′", semitones: 31, note: "Nineteenth (larigot)" },
  { id: "1", label: "1′", semitones: 36, note: "Twenty-second" },
];

export function rankById(id: string): Rank {
  const found = RANKS.find((r) => r.id === id);
  if (!found) throw new Error(`unknown rank: ${id}`);
  return found;
}

/** Absolute zero in degrees Celsius, for the speed-of-sound relation. */
const KELVIN_OFFSET = 273.15;

/**
 * Cents a flue pipe's pitch shifts when the air temperature moves from
 * `fromCelsius` to `toCelsius`.
 *
 * A flue pipe's frequency is set by the speed of sound in the air column,
 * which goes as sqrt(absolute temperature). Around room temperature this works
 * out near +3 cents per degree — enough that a rank tuned in a cold church at
 * 8am is noticeably sharp by the time a full congregation has warmed the place.
 *
 * Reeds behave differently: the vibrating tongue dominates, so they barely move
 * with temperature. That is precisely why reeds and flues drift apart.
 */
export function temperatureCents(fromCelsius: number, toCelsius: number): number {
  const from = fromCelsius + KELVIN_OFFSET;
  const to = toCelsius + KELVIN_OFFSET;
  if (from <= 0 || to <= 0) return 0;
  return 600 * Math.log2(to / from);
}

/** Frequency multiplier for the same temperature change. */
export function temperatureRatio(fromCelsius: number, toCelsius: number): number {
  return Math.pow(2, temperatureCents(fromCelsius, toCelsius) / 1200);
}
